import Service from '@/Network/Services/Service'
import { ServiceError, getApiErrorStatus } from '@/utils/apiError'
import request from '@/Network/Requests/request'
import type {
  ListPostStationEvaluationsParams,
  PostStationHistoryParams,
  RevertPostStationEvaluationPayload
} from '@/Network/Types/Requests/PostStationRequests'
import type {
  ListPostStationEvaluationsResponse,
  ListPostStationHistoryResponse,
  PostStationDomainDetailResponse,
  PostStationDomainKey,
  PostStationScanStatusResponse,
  PostStationStationEvaluationResponse,
  RevalidatePostStationEvaluationResponse,
  RevertPostStationEvaluationResponse
} from '@/Network/Types/Responses/PostStationResponses'

/** 404/405/501 = rota ainda não deployada; o web deploya antes do backend. */
const BACKEND_WITHOUT_ROUTE_STATUSES = [404, 405, 501]

/**
 * F4 — filtro "Reg. Plantão" do grid (`EffectiveRegistrationStatus[]`).
 *
 * Declarado aqui, e não em `PostStationRequests.ts`, porque aquele arquivo não
 * está no recorte desta task; quando alguém consolidar, é mover uma linha.
 * Array repetível, como `healthUnitIds`/`scheduleIds`.
 */
export type ListPostStationEvaluationsParamsF4 = ListPostStationEvaluationsParams & {
  registrationStatus?: string[]
}

/** Recorte do `GET /post-station/registration-block-impact`. */
export interface RegistrationBlockImpactParams {
  scheduleId: number
  from: string
  to: string
}

/**
 * "Quantos plantões deste recorte o gate do registro estaria bloqueando" — o
 * número que o gestor vê ANTES de ligar `postStationBlockOnRegistration` na
 * política (M5, Q21).
 *
 * `byStatus` é `Record<string, number>` de propósito: um estado novo do
 * backend precisa aparecer na tela, não sumir do total.
 */
export interface RegistrationBlockImpactResponse {
  total: number
  blocked: number
  byStatus: Record<string, number>
}

export default class PostStationService extends Service {
  prefix: string

  constructor() {
    super()
    this.prefix = '/post-station'
  }

  async listEvaluations(
    params?: ListPostStationEvaluationsParamsF4
  ): Promise<ListPostStationEvaluationsResponse> {
    try {
      const { data } = await request.get<ListPostStationEvaluationsResponse>(
        this.getCompletePath('/evaluations'),
        params
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar o painel de pós-plantão')
    }
  }

  /**
   * F4 — impacto do gate do registro no recorte, para a tela dizer "ligar isto
   * hoje bloquearia N de M plantões" antes de o gestor virar a chave.
   *
   * **A rota está sendo escrita na T9 do backend.** Enquanto ela não sobe (e
   * em qualquer ambiente que rode um binário anterior), 404/405/501 devolvem
   * `null` em vez de erro: a seção da política simplesmente não mostra o
   * número. Um 403/500 continua subindo — é falha de verdade e o gestor
   * precisa saber.
   */
  async getRegistrationBlockImpact(
    params: RegistrationBlockImpactParams
  ): Promise<RegistrationBlockImpactResponse | null> {
    try {
      const { data } = await request.get<RegistrationBlockImpactResponse>(
        this.getCompletePath('/registration-block-impact'),
        params
      )
      return data ?? null
    } catch (e: unknown) {
      const status = getApiErrorStatus(e)
      if (status && BACKEND_WITHOUT_ROUTE_STATUSES.includes(status)) {
        console.debug('[PostStation] rota de impacto do gate indisponível; seção segue sem ela', e)
        return null
      }
      throw this.handleError(e, 'Não foi possível calcular o impacto do bloqueio por registro')
    }
  }

  async getScanStatus(): Promise<PostStationScanStatusResponse> {
    try {
      const { data } = await request.get<PostStationScanStatusResponse>(
        this.getCompletePath('/scan-status')
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar o status da varredura de pós-plantão')
    }
  }

  async getDomainDetail(
    evaluationId: number,
    domainKey: PostStationDomainKey
  ): Promise<PostStationDomainDetailResponse> {
    try {
      const { data } = await request.get<PostStationDomainDetailResponse>(
        this.getCompletePath(`/evaluations/${evaluationId}/domains/${domainKey}`)
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os detalhes do domínio da avaliação')
    }
  }

  async getHistory(
    evaluationId: number,
    params?: PostStationHistoryParams
  ): Promise<ListPostStationHistoryResponse> {
    try {
      const { data } = await request.get<ListPostStationHistoryResponse>(
        this.getCompletePath(`/evaluations/${evaluationId}/history`),
        params
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar o histórico da avaliação')
    }
  }

  /**
   * Reverte a aprovação da avaliação (`APPROVED` → `REVERTED`).
   * Corresponde a: POST /post-station/evaluations/:evaluationId/revert
   */
  async revertEvaluation(
    evaluationId: number,
    payload: RevertPostStationEvaluationPayload
  ): Promise<RevertPostStationEvaluationResponse> {
    try {
      const { data } = await request.post<RevertPostStationEvaluationResponse>(
        this.getCompletePath(`/evaluations/${evaluationId}/revert`),
        payload
      )
      return data
    } catch (e: unknown) {
      // 403 = união OU dos 3 checks do backend negou (schedule / post_station_company
      // / post_station_health_unit) — mensagem específica em vez da genérica de
      // `handleError`, que o backend não preenche para 403 (BUG-PP-08).
      if (getApiErrorStatus(e) === 403) {
        throw new ServiceError('Você não tem permissão para reverter esta avaliação.', e)
      }
      throw this.handleError(e, 'Não foi possível reverter a aprovação da avaliação do plantão')
    }
  }

  /**
   * Revalida manualmente a avaliação revertida, reexecutando os 5 domínios
   * (`REVERTED` → `APPROVED` | `REVIEW` | `UNDER_VALIDATION`).
   * Corresponde a: POST /post-station/evaluations/:evaluationId/revalidate
   */
  async revalidateEvaluation(
    evaluationId: number
  ): Promise<RevalidatePostStationEvaluationResponse> {
    try {
      const { data } = await request.post<RevalidatePostStationEvaluationResponse>(
        this.getCompletePath(`/evaluations/${evaluationId}/revalidate`),
        {}
      )
      return data
    } catch (e: unknown) {
      // 403 = união OU dos 3 checks do backend negou — mesma lógica de
      // `revertEvaluation` (BUG-PP-08).
      if (getApiErrorStatus(e) === 403) {
        throw new ServiceError('Você não tem permissão para revalidar este plantão.', e)
      }
      throw this.handleError(e, 'Não foi possível revalidar a avaliação do plantão')
    }
  }

  /**
   * Avaliação de um plantão pelo id do `Station`, com a última reversão.
   * Corresponde a: GET /post-station/stations/:stationId/evaluation
   *
   * Devolve `null` no 404 em vez de lançar: "plantão sem avaliação de
   * pós-plantão" é estado ESPERADO do domínio (plantão antigo, vaga aberta,
   * varredura ainda não executada) e não uma falha — o consumidor simplesmente
   * não renderiza o card. Lançar aqui obrigaria a UI a engolir TODOS os erros
   * para não exibir toast num caso normal, escondendo falhas reais (500, rede,
   * 403), que continuam sendo lançadas com a mensagem do backend.
   */
  async getStationEvaluation(
    stationId: number
  ): Promise<PostStationStationEvaluationResponse | null> {
    try {
      const { data } = await request.get<PostStationStationEvaluationResponse>(
        this.getCompletePath(`/stations/${stationId}/evaluation`)
      )
      return data
    } catch (e: unknown) {
      // `getApiErrorStatus` é o narrowing tipado da camada Network — dá
      // acesso ao status HTTP sem `any`.
      // fallback intencional: sem avaliação é estado válido do domínio (ver docblock).
      if (getApiErrorStatus(e) === 404) return null
      throw this.handleError(e, 'Não foi possível carregar a avaliação do plantão')
    }
  }
}
